import { useCallback, useEffect, useRef } from "react"

type CaptchaSuccessCallback = (captcha: string) => void
type CaptchaFailCallback = (success: boolean) => void

type AliyunCaptchaInstance = {
  hide: () => void
}

type AliyunCaptchaOptions = {
  SceneId: string
  button: string
  fail: CaptchaFailCallback
  getInstance: (instance: AliyunCaptchaInstance) => void
  language: "cn"
  mode: "popup"
  prefix: string
  slideStyle: {
    height: number
    width: number
  }
  success: CaptchaSuccessCallback
}

declare global {
  interface Window {
    initAliyunCaptcha?: (options: AliyunCaptchaOptions) => void
  }
}

const captchaElementId = "videoWebMountCaptcha"

export const useCaptcha = (
  successCallback: CaptchaSuccessCallback,
  failCallback?: CaptchaFailCallback,
) => {
  const instanceRef = useRef<AliyunCaptchaInstance | null>(null)
  const mountDomRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let mountDom = document.querySelector<HTMLDivElement>(
      `#${captchaElementId}`,
    )
    const ownsMountDom = !mountDom

    if (!mountDom) {
      mountDom = document.createElement("div")
      mountDom.id = captchaElementId
      mountDom.hidden = true
      document.body.append(mountDom)
    }

    mountDomRef.current = mountDom

    return () => {
      instanceRef.current?.hide()
      instanceRef.current = null
      document.getElementById("aliyunCaptcha-mask")?.remove()
      document.getElementById("aliyunCaptcha-window-popup")?.remove()
      if (ownsMountDom) mountDom.remove()
      mountDomRef.current = null
    }
  }, [])

  const triggerCaptcha = useCallback(() => {
    if (!window.initAliyunCaptcha) {
      failCallback?.(false)
      return
    }

    window.initAliyunCaptcha({
      SceneId: "1cnjxfk5",
      button: `#${captchaElementId}`,
      fail: failCallback ?? (() => {}),
      getInstance: (instance) => {
        instanceRef.current = instance
        mountDomRef.current?.click()
      },
      language: "cn",
      mode: "popup",
      prefix: "betzeb",
      slideStyle: {
        height: 40,
        width: Math.min(360, window.innerWidth - 48),
      },
      success: successCallback,
    })
  }, [failCallback, successCallback])

  const hideCaptcha = useCallback(() => {
    instanceRef.current?.hide()
  }, [])

  return {
    hideCaptcha,
    triggerCaptcha,
  }
}
